import { useEffect, useRef } from 'react';
import { breakpoints, Button, Stack } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import type { TripType } from '@eti/schema-types';
import { css } from '@eti/styles';
import { Plus } from '@phosphor-icons/react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { DEPARTURE_DATE, DESTINATION, MULTI_BOUND, ORIGIN } from '../constants/formFields';
import { MAX_BOUNDS } from '../constants/multistop';
import type { DateFormat } from '../types/dateFormat';
import BoundHeader from './BoundHeader';
import Bound from './Bound';

const addBoundButtonStyles = css`
  width: 100%;

  @media (min-width: ${breakpoints._768}) {
    width: fit-content;
  }
`;

type BoundsProps = {
  dateFormat: DateFormat;
  selectedTripType: typeof TripType.MultiStop | typeof TripType.OneWay | typeof TripType.Return;
};

const Bounds = ({ dateFormat, selectedTripType }: BoundsProps) => {
  const { t } = useTranslation();
  const addBoundButtonRef = useRef<HTMLButtonElement>(null);
  const lastBoundHeadingRef = useRef<HTMLHeadingElement>(null);
  const shouldFocusLastBound = useRef(false);
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: MULTI_BOUND,
  });

  const hasRemoveButtons = fields.length > 2;

  const onAdd = () => {
    shouldFocusLastBound.current = true;
    append({ [ORIGIN]: null, [DESTINATION]: null, [DEPARTURE_DATE]: null });
  };

  useEffect(() => {
    if (shouldFocusLastBound.current) {
      shouldFocusLastBound.current = false;
      lastBoundHeadingRef.current?.focus();
    }
  }, [fields.length]);

  const handleOnRemove = (boundId?: number) => {
    remove(boundId);
    addBoundButtonRef.current?.focus();
  };

  return (
    <>
      {fields.map((field, index) => (
        <Stack key={field.id}>
          <BoundHeader
            boundIndex={index}
            headingRef={index === fields.length - 1 ? lastBoundHeadingRef : undefined}
            onRemove={handleOnRemove}
          />
          <Bound
            boundIndex={index}
            dateFormat={dateFormat}
            hasRemoveButtons={hasRemoveButtons}
            onRemove={handleOnRemove}
            selectedTripType={selectedTripType}
          />
        </Stack>
      ))}
      {fields.length < MAX_BOUNDS && (
        <Button
          className={addBoundButtonStyles}
          data-testid="searchForm-multiStop-addTrip-button"
          iconPrefix={<Plus />}
          onClick={onAdd}
          ref={addBoundButtonRef}
          size="small"
        >
          {t('Air.Search.MultiBound.AddBoundButton.Text')}
        </Button>
      )}
    </>
  );
};

export default Bounds;
